// The board's column vocabulary, and the ONE place a task's board lane is read.
//
// The board's only column vocabulary is the five real State GROUPS (STATE_GROUPS,
// constants.ts — the same `task_states.group` values: backlog / unstarted /
// started / completed / cancelled), so `ColumnKey` IS `StateGroupKey`.
//
// READ side: a task's group arrives ON THE WIRE (`Task.stateGroup`, denormalized
// by the relay from the row's `task_states.group`). The UI no longer derives it
// from the legacy `tasks.status` string: that mirror is LOSSY — `backlog` and
// `unstarted` both mirror to "pending", so Backlog-vs-To Do could never be told
// apart — and the derivation had to be kept byte-identical to the server's own
// mapStatusToStateGroup by hand, which is exactly the drift this removes.
//
// WRITE side: `statusForColumn` is the value to WRITE when moving a task into a
// column in a project with NO state catalog to resolve a real stateId against.
// It stays until `tasks.status` is dropped, because a stateless project has
// nothing else to write. It must never be the bare group name (a valid READ-side
// synonym, but NOT one of the server's stored status literals — writing it raw
// would leave an un-mirrored status outside that set, unfindable by later
// exact-match status filters). Each group maps to ONE canonical stored literal.
import { STATE_GROUPS, type StateGroupKey } from "./constants.js";

export type ColumnKey = StateGroupKey;

// The task fields the lane resolver reads. A full `Task` is assignable; a bare
// `{ stateGroup }` literal works for tests.
export interface LanedTask {
  stateGroup?: StateGroupKey | null;
}

// A task's board lane, read straight off the wire. Total by construction (a
// bucketer must always place a task somewhere): a task whose payload carries no
// group at all — a relay older than the field — lands in "unstarted", the
// natural "not placed yet" column. Callers that HAVE the project's state catalog
// should prefer resolving `task.stateId` through it (view.ts stateGroupOf): the
// catalog reflects an optimistic local move a beat before the server echoes the
// new group back.
export function stateGroupOfTask(task: LanedTask): ColumnKey {
  return task.stateGroup ?? "unstarted";
}

// The literal `tasks.status` value to WRITE when a stateless write path moves a
// task into `column`. One canonical stored literal per group, chosen to match
// what this board has always written for the groups that already had a column
// ("todo" for unstarted, "in_progress" for started, "done" for completed) plus
// the two the legacy vocabulary was missing (backlog, cancelled — both already
// valid stored literals, real synonyms of their own group).
const STATUS_FOR_COLUMN: Record<ColumnKey, string> = {
  backlog: "backlog",
  unstarted: "todo",
  started: "in_progress",
  completed: "done",
  cancelled: "cancelled",
};

export function statusForColumn(column: ColumnKey): string {
  return STATUS_FOR_COLUMN[column];
}

// Is `value` one of the five real state groups? The wire carries `stateGroup` as
// a plain string, so this is the narrowing gate every inbound task passes through
// (client.ts mapRawTask) — an unrecognized value is dropped rather than trusted,
// so a bad relay can never put a card in a column that does not exist.
export function isStateGroupKey(value: unknown): value is StateGroupKey {
  return typeof value === "string" && STATE_GROUPS.some((g) => g.key === value);
}
